import React from "react";
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
} from "@chakra-ui/react";
import { FaRegQuestionCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const P = "features.InvestmentScreening.Popover.rationalizationImprovementTotal";
const C = "common.label";

interface Props {
  isSimpleMode?: boolean;
}

/**
 * 改善効果の合計の項目説明ポップオーバー
 *
 * 使用例:
 * <RationalizationImprovementTotalExplanationPopover isSimpleMode={isSimpleMode} />
 */
export const RationalizationImprovementTotalExplanationPopover: React.FC<Props> = ({ isSimpleMode = false }) => {
  const { t } = useTranslation();
  return (
    <Popover placement="right">
      <PopoverTrigger>
        <button style={{ background: 'none', border: 'none', padding: 0 }}>
          <FaRegQuestionCircle size="30px" cursor="pointer" />
        </button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="800px" maxH="600px" overflowY="auto" bgColor="#fffff0">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold">{t(`${P}.title`)}</PopoverHeader>
          <PopoverBody>
            <Box mb={4}>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th w="150px">{t(`${C}.calculationMethod`)}</Th>
                    <Th>{t(`${C}.description`)}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td fontWeight="bold">{t(`${P}.differenceCalcName`)}</Td>
                    <Td>{t(`${P}.differenceCalcDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">{t(`${P}.processConsolidationName`)}</Td>
                    <Td>{t(`${P}.processConsolidationDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">{t(`${P}.currencyConversionName`)}</Td>
                    <Td>{t(`${P}.currencyConversionDesc`)}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            <Box mb={4}>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th w="60px">{t(`${C}.no`)}</Th>
                    <Th w="180px">{t(`${C}.indicatorItem`)}</Th>
                    <Th>{t(`${C}.description`)}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>1</Td>
                    <Td fontWeight="bold">{t(`${P}.productionCountName`)}</Td>
                    <Td>{t(`${P}.productionCountDesc`)}</Td>
                  </Tr>
                  {isSimpleMode && (
                    <Tr>
                      <Td>2</Td>
                      <Td fontWeight="bold">{t(`${P}.salesQuantityName`)}</Td>
                      <Td>{t(`${P}.salesQuantityDesc`)}</Td>
                    </Tr>
                  )}
                  <Tr>
                    <Td>-</Td>
                    <Td fontWeight="bold">{t(`${P}.customItemName`)}</Td>
                    <Td>{t(`${P}.customItemDesc`)}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            <Box>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th w="60px">{t(`${C}.no`)}</Th>
                    <Th w="180px">{t(`${C}.costItem`)}</Th>
                    <Th>{t(`${C}.description`)}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>1</Td>
                    <Td fontWeight="bold">{t(`${P}.laborCostName`)}</Td>
                    <Td>{t(`${P}.laborCostDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td>2</Td>
                    <Td fontWeight="bold">{t(`${P}.repairCostName`)}</Td>
                    <Td>{t(`${P}.repairCostDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td>3</Td>
                    <Td fontWeight="bold">{t(`${P}.consumablesCostName`)}</Td>
                    <Td>{t(`${P}.consumablesCostDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td>4</Td>
                    <Td fontWeight="bold">{t(`${P}.maintenanceCostName`)}</Td>
                    <Td>{t(`${P}.maintenanceCostDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td>5</Td>
                    <Td fontWeight="bold">{t(`${P}.scrapCostName`)}</Td>
                    <Td>{t(`${P}.scrapCostDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td>6</Td>
                    <Td fontWeight="bold">{t(`${P}.equipmentDepreciationName`)}</Td>
                    <Td>{t(`${P}.equipmentDepreciationDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td>7</Td>
                    <Td fontWeight="bold">{t(`${P}.otherDepreciationName`)}</Td>
                    <Td>{t(`${P}.otherDepreciationDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td>-</Td>
                    <Td fontWeight="bold">{t(`${P}.customItemName`)}</Td>
                    <Td>{t(`${P}.customItemDesc`)}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
